import React, { useEffect, useState } from "react";
import CalenderDropdown from "@_common/CalenderDropdown";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import moment from "moment";
import qs from "query-string";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getValue } from "@utils/lodash";
import {
  getManageAuditScoreFilter,
  vendorReportsAccount,
} from "@services/reports.service";
import Loader from "@components/common/Loader";
import ScrollableReactTable from "@components/ScrollableDatatable";
import Notfound from "@components/Not-Found";
import PaginationPage from "@components/pagination/pagination";
import { QueryRequestHelper } from "@_common/query-request-helper";
import { tableScoreColors } from "@_common/colors";
import { formatText, numberFormatter } from "@_common/text-helper";
import SearchHeader from "@components/common/SearchHeader";
import ReportsFilterForm from "./filter";
import "./filter-form.scss";
import SearchableDropdownSelect from "@_common/SearchableDropdown";

interface ReportsVendorHistoryProps {}
const ReportsVendorHistory: React.FunctionComponent<
  ReportsVendorHistoryProps
> = () => {
  let navigate = useNavigate();

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */
  const [date, setDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const [check, setCheck] = useState(false);
  const [isFilterVisible, setFilterVisibility] = React.useState(false);
  const [request, setRequest] = useState<any>({
    state_id: "",
    city_id: "",
    // region_id: "",
    group_id: "",
    location_id: "",
    // vendor_id: "",
    audit_type: "",
  });

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0) {
      getData();
      setDate(new Date());
      // getRegionsList("", "");
      getGroupsList("", "");
      getStatesList("", "", "");
      getCitiesList("", "", "", "");
      getLocationsList("", "", "", "", "");
      // getVendorList("", "", "", "", "", "", "");
      getAuditTypeList("", "", "", "", "", "");
    }
  }, []);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0) {
      getData();
      // getRegionsList("", "");
      getGroupsList("", "");
      getStatesList("", "", "");
      getCitiesList("", "", "", "");
      getLocationsList("", "", "", "", "");
      // getVendorList("", "", "", "", "", "", "");
      getAuditTypeList("", "", "", "", "", "");

      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }

      if (params.limit) {
        setLimit(parseInt(params.limit));
      }

      if (params.date) {
        setDate(new Date(params.date));
      } else {
        setDate(new Date());
      }
    }
  }, [window.location.href]);

  /* -------------------------------------------------------------------------- */
  /*                               Date section                             */
  /* -------------------------------------------------------------------------- */
  const handleSubmitDate = () => {
    let dates = moment(date).format("YYYY-MM-DD");
    if (dates && dates !== "Invalid date") {
      let payload: any = {
        date: dates,
      };
      // removing null values
      Object.keys(payload).forEach((key) => {
        if (!payload[key] || payload[key] === undefined) {
          delete payload[key];
        }
      });
      let queryRequest = qs.stringify(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
    } else {
      toast.error("Please select date");
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */
  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    let user = JSON.parse(getValue(localStorage, `user`, {}));
    let vendor_id = getValue(user, `vendor.id`, "");
    let user_id = getValue(user, `id`, "");
    let payload: any = {
      month: params.date
        ? params.date.split("-")[1]
        : date
        ? moment(date).format("MM")
        : moment(dates).format("MM"),
      year: params.date
        ? params.date.split("-")[0]
        : date
        ? moment(date).format("YYYY")
        : moment(dates).format("YYYY"),
      // vendor_id: vendor_id,
      user_id: user_id,
      type: "YEAR_HISTORY",
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : 100,
      search_text: params.search_text,
      sort_by: params.sort_by,
      is_redirect: true,
      // region_id: params.region_id,
      group_id: params.group_id,
      location_id: params.location_id,
      vendor_id: params.vendor_id ? params.vendor_id : vendor_id,
      // audit_type: params.audit_type,
      state_id: getValue(params, `state_id`, ""),
      city_id: getValue(params, `city_id`, ""),
    };
    // removing null values
    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === undefined) {
        delete payload[key];
      }
    });
    let queryRequest = qs.stringify(payload);
    setIsLoading(true);
    try {
      let resp = await vendorReportsAccount(queryRequest);
      if (resp) {
        setIsLoading(false);
        setList(getValue(resp, `data.data`, []));
        setTotalCount(getValue(resp, `data.pagination.total_records`, 0));
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               React Datatable                              */
  /* -------------------------------------------------------------------------- */
  const searchParams = new URLSearchParams(window.location.search);
  const paramsNew = Object.fromEntries(searchParams.entries());

  const dateParam = paramsNew?.date ? new Date(paramsNew.date) : new Date(); // Get the date from params or use the current date

  const getMonthNameWithYear = (date: Date, offset: number) => {
    const adjustedDate = new Date(date);
    adjustedDate.setMonth(date.getMonth() - offset); // Subtract months to get previous month
    const month = adjustedDate.toLocaleString("default", { month: "short" });
    const year = adjustedDate.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  // Use the new function to get month-year combinations
  const currentMonthName = getMonthNameWithYear(dateParam, 0);
  const prevMonth1Name = getMonthNameWithYear(dateParam, 1);
  const prevMonth2Name = getMonthNameWithYear(dateParam, 2);
  const prevMonth3Name = getMonthNameWithYear(dateParam, 3);

  const columns1 = React.useMemo(
    () => [
      {
        Header: "Site Name",
        accessor: "location_name",
        width: 150,
        Cell: ({ row }: any) => {
          return <>{row.values.location_name}</>;
        },
      },

      {
        Header: `${currentMonthName} Score`,
        accessor: "score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.score`, 0)
                    ? parseFloat(getValue(row, `values.score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.score`, null) === null
                ? "- -"
                : `${getValue(row, `values.score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth1Name} Score`,
        accessor: "prev_month_1_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_1_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_1_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_1_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_1_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth2Name} Score`,
        accessor: "prev_month_2_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_2_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_2_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_2_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_2_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth3Name} Score`,
        accessor: "prev_month_3_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_3_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_3_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_3_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_3_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: "Vendors",
        accessor: "vendor",
        width: 80,
      },
      {
        Header: "Absent",
        accessor: "absent_sites",
        width: 100,
      },
      {
        Header: "Submited Checkpoint",
        accessor: "uploaded",
        width: 140,
      },
      {
        Header: "Not Submited Checkpoint",
        accessor: "not_uploaded",
        width: 160,
      },
      {
        Header: "Headcount",
        accessor: "manpower",
        width: 80,
        Cell: ({ row }: any) => {
          return (
            <div className="headcount-button">
              {getValue(row, `values.manpower`, 0)
                ? numberFormatter(getValue(row, `values.manpower`, 0))
                : 0}
            </div>
          );
        },
      },
    ],
    [paramsNew.date]
  );

  const year = paramsNew?.date
    ? new Date(paramsNew.date).getFullYear()
    : new Date().getFullYear();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const columns = React.useMemo(
    () => [
      // {
      //   Header: "Vendor Name",
      //   accessor: "vendor_name",
      //   width: 180,
      // },
      ...months.map((month) => ({
        Header: `${month} ${year}`,
        accessor: `${month.toLowerCase()}_score`, // Example: jan_score
        width: 90,
        Cell: ({ row }: any) => {
          const score = getValue(
            row,
            `values.${month.toLowerCase()}_score`,
            null
          );
          const parsedScore = score !== null ? parseFloat(score) : null;

          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(parsedScore ?? 0), // Use color logic
              }}
            >
              {parsedScore === null ? "- -" : `${parsedScore}%`}
            </div>
          );
        },
      })),
    ],
    [paramsNew.date]
  );

  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      list.length > 0
        ? list.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {},
      };
    }
    return {};
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      date: params.date,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Filter section                               */
  /* -------------------------------------------------------------------------- */

  const reset = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      page_no: params.page_no,
      search_text: getValue(params, `search_text`, ""),
      limit: getValue(params, `limit`, ""),
      date: getValue(params, `date`, ""),
      view_type: params.view_type, // Preserve view_type
    };
    setRequest({
      ...request,
      state_id: "",
      city_id: "",
      // region_id: "",
      group_id: "",
      location_id: "",
      // vendor_id: "",
      audit_type: "",
    });
    // setGroupsList([]);
    // setStateList([]);
    // setCityList([]);
    // setlocationsList([]);
    // setvendorsList([]);
    // setAuditTypeList([]);
    // getRegionsList("", "");
    // getGroupsList("", "", "");
    // getStatesList("", "", "", "");
    // getCitiesList("", "", "", "", "");
    // getLocationsList("","","","","","");
    // getVendorList("","","","","","","");
    // getAuditTypeList("","","","","","","","");
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    getData();
    // setFilterVisibility(false);
  };

  const handleSubmitSmartFilter = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      country_id: getValue(request, `country_id`, ""),
      state_id: getValue(request, `state_id`, ""),
      city_id: getValue(request, `city_id`, ""),
      // region_id: getValue(request, `region_id`, ""),
      group_id: getValue(request, `group_id`, ""),
      location_id: getValue(request, `location_id`, ""),
      // vendor_id: getValue(request, `vendor_id`, ""),
      audit_type: getValue(request, `audit_type`, ""),
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    // handleFilter();
    setFilterVisibility(false);
  };

  /* -------------------------------------------------------------------------- */
  /*                    smart Filter API section                                */
  /* -------------------------------------------------------------------------- */

  const [regionList, setRegionList] = useState([]);
  const [regionLoading, setRegionLoading] = useState(false);

  const getRegionsList = async (search_text: string, id: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        type: "REGION",
        search_text: search_text,
        region_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };
      setRegionLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setRegionList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `region_id`, ""),
            label: getValue(item, `region_name`, ""),
            value: getValue(item, `region_name`, ""),
          }))
        );
        setRegionLoading(false);
      } else {
        setRegionLoading(false);
      }
    } catch (error) {
      setRegionLoading(false);
    }
    if (!search_text) {
      // setRequest({
      //   ...request,
      //   region_id: "",
      // });
      //   getGroupsList("", "");
      //   setGroupsList([]);
      //   getLocationsList("", "", "");
      //   setlocationsList([]);
      //   getVendorList("", "", "", "");
      //   setvendorsList([]);
    }
  };

  const [groupsList, setGroupsList] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const getGroupsList = async (
    search_text: string,
    // region_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        type: "GROUP",
        search_text: search_text,
        group_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };
      setGroupLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setGroupsList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `group_id`, ""),
            label: getValue(item, `group_name`, ""),
            value: getValue(item, `group_name`, ""),
          }))
        );
        setGroupLoading(false);
      } else {
        setGroupLoading(false);
      }
    } catch (error) {
      setGroupLoading(false);
    }
    if (!search_text) {
      // setRequest({
      //   ...request,
      //   group_id: null,
      // });
      //   getLocationsList("", "", "");
      //   setlocationsList([]);
      //   getVendorList("", "", "", "");
      //   setvendorsList([]);
    }
  };

  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const getStatesList = async (
    search_text: string,
    // region_id: string,
    group_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        group_id: group_id,
        type: "STATE",
        search_text: search_text,
        state_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };

      setStateLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setStateList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `state_id`, ""),
            label: getValue(item, `state_name`, ""),
            value: getValue(item, `state_name`, ""),
          }))
        );
        setStateLoading(false);
      } else {
        setStateLoading(false);
      }
    } catch (error) {
      setStateLoading(false);
    }
  };

  const [cityList, setCityList] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const getCitiesList = async (
    search_text: string,
    // region_id: string,
    group_id: string,
    state_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        group_id: group_id,
        type: "CITY",
        search_text: search_text,
        state_id: state_id,
        city_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };

      setCityLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setCityList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `city_id`, ""),
            label: getValue(item, `city_name`, ""),
            value: getValue(item, `city_name`, ""),
          }))
        );
        setCityLoading(false);
      } else {
        setCityLoading(false);
      }
    } catch (error) {
      setCityLoading(false);
    }
  };

  const [locationsList, setlocationsList] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const getLocationsList = async (
    search_text: string,
    // region_id: string,
    group_id: string,
    state_id: string,
    city_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        group_id: group_id,
        type: "LOCATION",
        search_text: search_text,
        state_id: state_id,
        city_id: city_id,
        location_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };
      setLocationLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setlocationsList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `location_id`, ""),
            label: getValue(item, `location_name`, ""),
            value: getValue(item, `location_name`, ""),
          }))
        );
        setLocationLoading(false);
      } else {
        setLocationLoading(false);
      }
    } catch (error) {
      setLocationLoading(false);
    }
  };

  const [vendorsList, setvendorsList] = useState([]);
  const [vendorLoading, setvendorLoading] = useState(false);
  const getVendorList = async (
    search_text: string,
    // region_id: string,
    group_id: string,
    state_id: string,
    city_id: string,
    location_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        group_id: group_id,
        state_id: state_id,
        city_id: city_id,
        location_id: location_id,
        type: "VENDOR",
        search_text: search_text,
        vendor_id: id,
        is_redirect: true,
        userType: "VENDOR",
      };
      setvendorLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setvendorsList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `vendor_id`, ""),
            label: getValue(item, `vendor_name`, ""),
            value: getValue(item, `vendor_name`, ""),
          }))
        );
        setvendorLoading(false);
      } else {
        setvendorLoading(false);
      }
    } catch (error) {
      setvendorLoading(false);
    }
  };

  const [auditTypeList, setAuditTypeList] = useState([]);
  const [auditTypeLoading, setAuditTypeLoading] = useState(false);
  const getAuditTypeList = async (
    search_text: string,
    // region_id: string,
    group_id: string,
    state_id: string,
    city_id: string,
    location_id: string,
    // vendor_id: string,
    id: string
  ) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    try {
      let payload = {
        month: params.date
          ? params.date.split("-")[1]
          : date
          ? moment(date).format("MM")
          : moment(dates).format("MM"),
        year: params.date
          ? params.date.split("-")[0]
          : date
          ? moment(date).format("YYYY")
          : moment(dates).format("YYYY"),
        // region_id: region_id,
        group_id: group_id,
        state_id: state_id,
        city_id: city_id,
        location_id: location_id,
        // vendor_id: vendor_id,
        type: "AUDIT_TYPE",
        search_text: search_text,
        audit_type: id,
        is_redirect: true,
        userType: "VENDOR",
      };
      setAuditTypeLoading(true);
      let queryRequest = QueryRequestHelper(payload);
      let resp = await getManageAuditScoreFilter(queryRequest);
      if (resp) {
        setAuditTypeList(
          getValue(resp, `data.vendors_checkpoint`, []).map((item: object) => ({
            ...item,
            id: getValue(item, `audit_status`, ""),
            label: formatText(getValue(item, `audit_status`, "")),
            value: getValue(item, `audit_status`, ""),
          }))
        );
        setAuditTypeLoading(false);
      } else {
        setAuditTypeLoading(false);
      }
    } catch (error) {
      setAuditTypeLoading(false);
    }
    if (!search_text) {
      // setRequest({
      //   ...request,
      //   vendor_id: "",
      // });
    }
  };

  let state_ID =
    getValue(stateList, `length`, 0) > 0 &&
    stateList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `state_id`, "")
    );

  let city_ID =
    getValue(cityList, `length`, 0) > 0 &&
    cityList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `city_id`, "")
    );
  let region_ID =
    getValue(regionList, `length`, 0) > 0 &&
    regionList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `region_id`, "")
    );

  let group_ID =
    getValue(groupsList, `length`, 0) > 0 &&
    groupsList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `group_id`, "")
    );

  let location_ID =
    getValue(locationsList, `length`, 0) > 0 &&
    locationsList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `location_id`, "")
    );

  let vendor_ID =
    getValue(vendorsList, `length`, 0) > 0 &&
    vendorsList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `vendor_id`, "")
    );

  let audit_type_ID =
    getValue(auditTypeList, `length`, 0) > 0 &&
    auditTypeList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `audit_type`, "")
    );

  const handleRemoveFilter = (name: string) => {
    if (name === "region_id") {
      setRequest({
        ...request,
        // region_id: "",
        group_id: "",
        state_id: "",
        city_id: "",
        location_id: "",
        // vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      getGroupsList("", "");
      getStatesList("", "", "");
      getCitiesList("", "", "", "");
      getLocationsList("", "", "", "", "");
      // getVendorList("", "", "", "", "", "", "");
      getAuditTypeList("", "", "", "", "", "");

      setGroupsList([]);
      setStateList([]);
      setCityList([]);
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }
    if (name === "group_id") {
      setRequest({
        ...request,
        group_id: "",
        state_id: "",
        city_id: "",
        location_id: "",
        // vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      getGroupsList("", "");
      setStateList([]);
      setCityList([]);
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    if (name === "state_id") {
      setRequest({
        ...request,
        state_id: "",
        city_id: "",
        location_id: "",
        // vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      getStatesList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        ""
      );
      setCityList([]);
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    if (name === "city_id") {
      setRequest({
        ...request,
        city_id: "",
        location_id: "",
        vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      getCitiesList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        getValue(request, `state_id`, ""),
        ""
      );
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    if (name === "location_id") {
      setRequest({
        ...request,
        location_id: "",
        vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      // getGroupsList("", getValue(request, `region_id`, ""), "");
      getLocationsList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        getValue(request, `state_id`, ""),
        getValue(request, `city_id`, ""),
        ""
      );
      setvendorsList([]);
      setAuditTypeList([]);
    }
    if (name === "vendor_id") {
      setRequest({
        ...request,
        vendor_id: "",
        audit_type: "",
      });
      // getRegionsList("", "");
      // getGroupsList("", getValue(request, `region_id`, ""), "");
      // getLocationsList(
      //   "",
      //   getValue(request, `region_id`, ""),
      //   getValue(request, `group_id`, ""),
      //   ""
      // );
      getVendorList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        getValue(request, `state_id`, ""),
        getValue(request, `city_id`, ""),
        getValue(request, `location_id`, ""),
        ""
      );
      setAuditTypeList([]);
    }
    if (name === "audit_type") {
      setRequest({
        ...request,
        audit_type: "",
      });
      // getRegionsList("", "");
      // getGroupsList("", getValue(request, `region_id`, ""), "");
      // getLocationsList(
      //   "",
      //   getValue(request, `region_id`, ""),
      //   getValue(request, `group_id`, ""),
      //   ""
      // );
      // getVendorList(
      //   "",
      //   getValue(request, `region_id`, ""),
      //   getValue(request, `group_id`, ""),
      //   getValue(request, `location_id`, ""),
      //   ""
      // );
      getAuditTypeList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),

        getValue(request, `state_id`, ""),
        getValue(request, `city_id`, ""),

        getValue(request, `location_id`, ""),
        // getValue(request, `vendor_id`, ""),
        ""
      );
    }
    // getRegionsList("", "");
    // getGroupsList("", "", "");
    // getStatesList("", "", "", "");
    // getCitiesList("", "", "", "", "");
    // getLocationsList("","","","","","");
    // getVendorList("","","","","","","");
    // getAuditTypeList("","","","","","","","");
  };

  const handleSelectFilter = (id: any, name: string) => {
    // if (name === "region_id") {
    //   getGroupsList("", id);
    //   setRequest({
    //     ...request,
    //     group_id: "",
    //     state_id: "",
    //     city_id: "",
    //     location_id: "",
    //     vendor_id: "",
    //     audit_type: "",
    //   });
    //   setGroupsList([]);
    //   setStateList([]);
    //   setCityList([]);
    //   setlocationsList([]);
    //   setvendorsList([]);
    //   setAuditTypeList([]);
    // }
    if (name === "group_id") {
      getStatesList("", id, "");
      setRequest({
        ...request,
        state_id: "",
        city_id: "",
        location_id: "",
        // vendor_id: "",
        audit_type: "",
      });
      setStateList([]);
      setCityList([]);
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    if (name === "state_id") {
      getCitiesList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        id,
        ""
      );
      setRequest({
        ...request,
        city_id: "",
        location_id: "",
        // vendor_id: "",
        audit_type: "",
      });
      setCityList([]);
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    if (name === "city_id") {
      getLocationsList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        getValue(request, `state_id`, ""),
        id,
        ""
      );
      setRequest({
        ...request,
        location_id: "",
        vendor_id: "",
        audit_type: "",
      });
      setlocationsList([]);
      setvendorsList([]);
      setAuditTypeList([]);
    }

    // if (name === "location_id") {
    //   getVendorList(
    //     "",
    //     // getValue(request, `region_id`, ""),
    //     getValue(request, `group_id`, ""),
    //     getValue(request, `state_id`, ""),
    //     getValue(request, `city_id`, ""),
    //     id,
    //     ""
    //   );
    //   setRequest({
    //     ...request,
    //     vendor_id: "",
    //     audit_type: "",
    //   });
    //   setvendorsList([]);
    //   setAuditTypeList([]);
    // }

    if (name === "location_id") {
      getAuditTypeList(
        "",
        // getValue(request, `region_id`, ""),
        getValue(request, `group_id`, ""),
        getValue(request, `state_id`, ""),
        getValue(request, `city_id`, ""),
        // getValue(request, `location_id`, ""),
        id,
        ""
      );
      setRequest({
        ...request,
        audit_type: "",
      });
      setAuditTypeList([]);
    }

    setRequest({
      ...request,
      [name]: id,
    });
  };

  return (
    <>
      <DashboardLayout>
        <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
          <h4 className="dashbaord-wrapper__header-title">Score History</h4>

          <div className="d-flex align-items-center">
            <CalenderDropdown
              onChange={setDate}
              value={date}
              handleSubmitDate={handleSubmitDate}
            />

            <div className="header-searchbar-wrapper ms-2">
              <SearchHeader
                searchVisible={false}
                addVisible={false}
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}
                smartFilter={true}
                setFilterFormVisibility={() => {
                  setFilterVisibility(!isFilterVisible);
                }}
                isFilterVisible={false}
              />
            </div>
          </div>
        </div>

        <div className="admin-wrapper position-relative">
          {/* {isFilterVisible && (

            
            <ReportsFilterForm
              setFilterFormVisibility={() => {
                setFilterVisibility(!isFilterVisible);
              }}
              reset={reset}
              handleSubmitSmartFilter={handleSubmitSmartFilter}
              // resetInput={resetInput}
              //region
              regionLoading={regionLoading}
              regionList={regionList}
              region_ID={region_ID}
              getRegionsList={getRegionsList}
              //group
              groupLoading={groupLoading}
              groupsList={groupsList}
              group_ID={group_ID}
              getGroupsList={getGroupsList}
              //state
              stateLoading={stateLoading}
              stateList={stateList}
              state_ID={state_ID}
              getStatesList={getStatesList}
              //city
              cityLoading={cityLoading}
              cityList={cityList}
              city_ID={city_ID}
              getCitiesList={getCitiesList}
              //location
              locationLoading={locationLoading}
              locationsList={locationsList}
              location_ID={location_ID}
              getLocationsList={getLocationsList}
              //configuration/vendors
              vendorLoading={vendorLoading}
              vendorsList={vendorsList}
              vendor_ID={vendor_ID}
              getVendorList={getVendorList}
              //audit_type
              auditTypeLoading={auditTypeLoading}
              auditTypeList={auditTypeList}
              audit_type_ID={audit_type_ID}
              getAuditTypeList={getAuditTypeList}

              request={request}

            /> */}
            <>
              <div className="filter-form-wrappper w-100 active filter-form-legal-update d-none">
                <div className="filter-form-wrappper__container">
                  <div className="d-flex align-items-center justify-content-between my-3">
                    <h6 className="filter-form-wrappper__title">Filter by</h6>
                    <img
                      src="/images/close-black.svg"
                      className="img-fluid cursor-pointer"
                      onClick={() => {
                        // reset();
                        // setFilterFormVisibility();
                        setFilterVisibility(false);
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-between gap">
                    <div className="filter-form-wrappper__location flex-1">
                      {/* <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          Region
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Select Region`}
                            options={regionList}
                            searchLoading={regionLoading}
                            handleChange={(val: any) =>
                              val
                                ? getRegionsList(val, "")
                                : handleRemoveFilter("region_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(
                                getValue(e, `id`, ""),
                                "region_id"
                              )
                            }
                            value={
                              Array.isArray(region_ID) && region_ID.length > 0
                                ? region_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"region_id"}
                          />
                        </div>
                      </div> */}

                      <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          Account
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Select Account`}
                            options={groupsList}
                            searchLoading={groupLoading}
                            handleChange={(val: any) =>
                              val
                                ? getGroupsList(
                                    val,
                                    getValue(request, `group_id`, "")
                                    // ""
                                  )
                                : handleRemoveFilter("group_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(
                                getValue(e, `id`, ""),
                                "group_id"
                              )
                            }
                            // value={group_ID}
                            value={
                              Array.isArray(group_ID) && group_ID.length > 0
                                ? group_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"group_id"}
                          />
                        </div>
                      </div>

                      <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          Site
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Select Site`}
                            options={locationsList}
                            searchLoading={locationLoading}
                            handleChange={(val: any) =>
                              val
                                ? getLocationsList(
                                    val,
                                    // getValue(request, `region_id`, ""),
                                    getValue(request, `group_id`, ""),
                                    getValue(request, `state_id`, ""),
                                    getValue(request, `city_id`, ""),
                                    ""
                                  )
                                : handleRemoveFilter("location_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(
                                getValue(e, `id`, ""),
                                "location_id"
                              )
                            }
                            // value={location_ID}
                            value={
                              Array.isArray(location_ID) && location_ID.length > 0
                                ? location_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"location_id"}
                            // removeClose
                          />
                        </div>
                      </div>
                    </div>

                    <div className="filter-form-wrappper__location flex-1">
                      <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          State
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Choose State`}
                            options={stateList}
                            searchLoading={stateLoading}
                            handleChange={(val: any) =>
                              val
                                ? getStatesList(
                                    val,
                                    // getValue(request, `region_id`, ""),
                                    getValue(request, `group_id`, ""),
                                    // getValue(props, `request.state_id`, ""),
                                    ""
                                  )
                                : handleRemoveFilter("state_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(
                                getValue(e, `id`, ""),
                                "state_id"
                              )
                            }
                            value={
                              Array.isArray(state_ID) && state_ID.length > 0
                                ? state_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"state_id"}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="filter-form-wrappper__location flex-1">
                      <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          City
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Choose City`}
                            options={cityList}
                            searchLoading={cityLoading}
                            handleChange={(val: any) =>
                              val
                                ? getCitiesList(
                                    val,
                                    // getValue(request, `region_id`, ""),
                                    getValue(request, `group_id`, ""),
                                    getValue(request, `state_id`, ""),
                                    // getValue(props, `request.city_id`, ""),
                                    ""
                                  )
                                : handleRemoveFilter("city_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(getValue(e, `id`, ""), "city_id")
                            }
                            // value={getValue(props, `city_ID[${0}]`, "")}
                            value={
                              Array.isArray(city_ID) && city_ID.length > 0
                                ? city_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"city_id"}
                          />
                        </div>
                      </div>

                      {/* <div className="filter-form-wrappper__location-form-group form-group">
                        <label
                          htmlFor="state"
                          className="filter-form-wrappper__label"
                        >
                          Vendor
                        </label>
                        <div className="filter-form-wrappper__location-input select-dd-new">
                          <SearchableDropdownSelect
                            placeholder={`Select Vendor`}
                            options={vendorsList}
                            searchLoading={vendorLoading}
                            handleChange={(val: any) =>
                              val
                                ? getVendorList(
                                    val,
                                    // getValue(request, `region_id`, ""),
                                    getValue(request, `group_id`, ""),
                                    getValue(request, `state_id`, ""),
                                    getValue(request, `city_id`, ""),
                                    getValue(request, `location_id`, ""),
                                    ""
                                  )
                                : handleRemoveFilter("vendor_id")
                            }
                            handleClick={(e: any) =>
                              handleSelectFilter(
                                getValue(e, `id`, ""),
                                "vendor_id"
                              )
                            }
                            value={
                              Array.isArray(vendor_ID) && vendor_ID.length > 0
                                ? vendor_ID[0]
                                : ""
                            }
                            label="label"
                            id="id"
                            name={"vendor_id"}
                            // removeClose
                            // disabled={disableVendor}
                          />
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>

                <div className="filter-form-wrappper__cta-wrapper">
                  <button
                    className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--search"
                    onClick={handleSubmitSmartFilter}
                  >
                    Search
                  </button>
                  <button
                    className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--reset"
                    onClick={reset}
                  >
                    Reset
                  </button>
                </div>
              </div>
              
              <div
                  className={`filter-slidder-wrapper  ${
                    isFilterVisible ? "filter-slidder-wrapper--active" : ""
                  }`}
                >
                  <div className="filter-form-wrappper w-100 active filter-form-legal-update">
                    <div className="filter-form-wrappper__container">
                      <div className="filter-header-wrapper d-flex align-items-center justify-content-between">
                        <div className="filter-header-left-wrapper d-flex align-items-center">
                          <div className="filter-close-wrapper">
                            <img
                              src="/images/close-circle.svg"
                              className="img-fluid cursor-pointer"
                              onClick={() => {
                                setFilterVisibility(false);
                              }}
                            />
                          </div>
                          <div className="filter-title-wrapper">
                            <h6 className="filter-form-wrappper__title">Filter</h6>
                          </div>

                          <div className="filter-module-details-wrapper">
                            <div className="filter-module-name">Score History</div>
                          </div>
                        </div>

                        <div className="filter-header-right-wrapper">
                          <div className="filter-form-wrappper__cta-wrapper p-0 d-flex">
                            <button
                              className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--reset"
                              onClick={reset}
                            >
                              <div className="d-flex align-items-center mt--3">
                                {/* <ResetSvgComponent size={24} color={"#5F6368"} />  */}
                                Reset
                              </div>
                            </button>

                            <button
                              className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--cancel"
                              onClick={() => {
                                setFilterVisibility(false);
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--search"
                              onClick={handleSubmitSmartFilter}
                            >
                              Search
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="filter-body-wrapper">
                        <div className="filter-body-card">
                          <div className="filter-card-title">Filter By</div>

                          <div className="d-flex justify-content-between flex-column">
                            <div className="filter-form-wrappper__location">
                              <div className="d-flex w-100 filter-field-main-wrap">
                                <div className="filter-form-wrappper__location-form-group form-group filter-field-children-wrap">
                                  <label
                                    htmlFor="state"
                                    className="filter-form-wrappper__label"
                                  >
                                    Account
                                  </label>
                                  <div className="filter-form-wrappper__location-input select-dd-new">
                                    <SearchableDropdownSelect
                                      placeholder={`Select Account`}
                                      options={groupsList}
                                      searchLoading={groupLoading}
                                      handleChange={(val: any) =>
                                        val
                                          ? getGroupsList(
                                              val,
                                              getValue(request, `group_id`, "")
                                              // ""
                                            )
                                          : handleRemoveFilter("group_id")
                                      }
                                      handleClick={(e: any) =>
                                        handleSelectFilter(
                                          getValue(e, `id`, ""),
                                          "group_id"
                                        )
                                      }
                                      // value={group_ID}
                                      value={
                                        Array.isArray(group_ID) && group_ID.length > 0
                                          ? group_ID[0]
                                          : ""
                                      }
                                      label="label"
                                      id="id"
                                      name={"group_id"}
                                    />
                                  </div>
                                </div>

                                <div className="filter-form-wrappper__location-form-group form-group filter-field-children-wrap">
                                <label
                                  htmlFor="state"
                                  className="filter-form-wrappper__label"
                                >
                                  State
                                </label>
                                <div className="filter-form-wrappper__location-input select-dd-new">
                                  <SearchableDropdownSelect
                                    placeholder={`Choose State`}
                                    options={stateList}
                                    searchLoading={stateLoading}
                                    handleChange={(val: any) =>
                                      val
                                        ? getStatesList(
                                            val,
                                            // getValue(request, `region_id`, ""),
                                            getValue(request, `group_id`, ""),
                                            // getValue(props, `request.state_id`, ""),
                                            ""
                                          )
                                        : handleRemoveFilter("state_id")
                                    }
                                    handleClick={(e: any) =>
                                      handleSelectFilter(
                                        getValue(e, `id`, ""),
                                        "state_id"
                                      )
                                    }
                                    value={
                                      Array.isArray(state_ID) && state_ID.length > 0
                                        ? state_ID[0]
                                        : ""
                                    }
                                    label="label"
                                    id="id"
                                    name={"state_id"}
                                  />
                                </div>
                                </div>
                              </div>

                              <div className="d-flex w-100 filter-field-main-wrap">
                                <div className="filter-form-wrappper__location-form-group form-group filter-field-children-wrap">
                                <label
                                  htmlFor="state"
                                  className="filter-form-wrappper__label"
                                >
                                  City
                                </label>
                                <div className="filter-form-wrappper__location-input select-dd-new">
                                  <SearchableDropdownSelect
                                    placeholder={`Choose City`}
                                    options={cityList}
                                    searchLoading={cityLoading}
                                    handleChange={(val: any) =>
                                      val
                                        ? getCitiesList(
                                            val,
                                            // getValue(request, `region_id`, ""),
                                            getValue(request, `group_id`, ""),
                                            getValue(request, `state_id`, ""),
                                            // getValue(props, `request.city_id`, ""),
                                            ""
                                          )
                                        : handleRemoveFilter("city_id")
                                    }
                                    handleClick={(e: any) =>
                                      handleSelectFilter(getValue(e, `id`, ""), "city_id")
                                    }
                                    // value={getValue(props, `city_ID[${0}]`, "")}
                                    value={
                                      Array.isArray(city_ID) && city_ID.length > 0
                                        ? city_ID[0]
                                        : ""
                                    }
                                    label="label"
                                    id="id"
                                    name={"city_id"}
                                  />
                                </div>
                                </div>

                                <div className="filter-form-wrappper__location-form-group form-group filter-field-children-wrap">
                                  <label
                                    htmlFor="state"
                                    className="filter-form-wrappper__label"
                                  >
                                    Site
                                  </label>
                                  <div className="filter-form-wrappper__location-input select-dd-new">
                                    <SearchableDropdownSelect
                                      placeholder={`Select Site`}
                                      options={locationsList}
                                      searchLoading={locationLoading}
                                      handleChange={(val: any) =>
                                        val
                                          ? getLocationsList(
                                              val,
                                              // getValue(request, `region_id`, ""),
                                              getValue(request, `group_id`, ""),
                                              getValue(request, `state_id`, ""),
                                              getValue(request, `city_id`, ""),
                                              ""
                                            )
                                          : handleRemoveFilter("location_id")
                                      }
                                      handleClick={(e: any) =>
                                        handleSelectFilter(
                                          getValue(e, `id`, ""),
                                          "location_id"
                                        )
                                      }
                                      // value={location_ID}
                                      value={
                                        Array.isArray(location_ID) &&
                                        location_ID.length > 0
                                          ? location_ID[0]
                                          : ""
                                      }
                                      label="label"
                                      id="id"
                                      name={"location_id"}
                                      // removeClose
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="filter-form-wrappper__cta-wrapper">
                      <button
                        className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--search"
                        onClick={handleSubmitSmartFilter}
                      >
                        Search
                      </button>
                      <button
                        className="filter-form-wrappper__cta-btn filter-form-wrappper__cta-btn--reset"
                        onClick={reset}
                      >
                        Reset
                      </button>
                    </div>
                    {/* <AddFilterNew /> */}
                  </div>
                </div>
            </>
           {/* )} */}
          {isLoading ? (
            <Loader />
          ) : getValue(list, `length`, 0) > 0 ? (
            <ScrollableReactTable
              columns={columns}
              data={list}
              getTableState={getTableState}
              // rowClick={rowClick}
              check={check}
              selectedIDs={selectedIDs}
              checkboxlogo
              getTrProps={getTrProps}
            />
          ) : (
            <>
              {getValue(list, `length`, 0) !== 0 ? (
                <>
                  <Notfound />
                </>
              ) : (
                <Notfound />
              )}
            </>
          )}
        </div>

        {/* Marking Pagination as a d-none because in api im not getting pagination */}
        <div className="d-flex justify-content-between">
          {getValue(list, `length`, 0) > 0 && (
            <img
              src="/images/profile-illustration.svg"
              className="img-fluid px-5 py-3"
            />
          )}
          {totalCount > 10 ? (
            <PaginationPage
              totalCount={totalCount}
              limit={limit}
              pageNumber={page_no}
              handleChangePagination={handleChangePagination}
              handleChangeLimit={handleChangeLimit}
            />
          ) : (
            <p className="show-rows-count mt-3 d-flex">
              Total Count:&nbsp;&nbsp;{" "}
              <p style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</p>
            </p>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default ReportsVendorHistory;
